import { FIXTURE_STATE_CONFIG } from "@/utils/constants.js";
import { REWARD_TYPE } from "@/hooks/contest/contestStatus.js";

/**
 * Config de la démo de prospection HOLY.
 *
 * Pour créer une démo pour une nouvelle marque :
 *   1. Duplique ce fichier  →  src/features/brandDemo/configs/newBrand.js
 *   2. Remplace les valeurs ci-dessous
 *   3. Passe la config à la route :  <BrandDemoPage config={NEW_BRAND_CONFIG} />
 */
export const HOLY_CONFIG = {

    /* ─── IDENTITÉ DE MARQUE ─────────────────────────────────────── */
    brand: {
        name: "HOLY",
        logoUrl: "https://fr.holy.com/cdn/shop/files/HOLY_Logo_1_280x.png?v=1758652144",
        websiteUrl: "https://fr.holy.com",

        fonts: {
            heading: "Anton",
            body: "Inter",
            googleFontsUrl: "https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;600;800;900&display=swap",
        },

        colors: {
            pageBg:        "#FFB6CE", // fond principal des écrans plein-page
            accent:        "#05D37C", // CTA primaire, badges de succès, points "moi"
            accentText:    "#000000", // texte sur fond accent
            highlight:     "#FFEC28", // badges secondaires (step badge, rank #1)
            highlightText: "#000000",
            goalFlash:     "#FF91A2", // couleur du flash "BUT !" + rank #3
            error:         "#FF91A2", // fond erreur formulaire
        },
    },

    /* ─── ÉCRAN D'ACCUEIL ────────────────────────────────────────── */
    welcome: {
        partner: {
            name: "YOUTUBER A",
            avatarUrl: "https://ui-avatars.com/api/?name=Y+A&background=000000&color=05D37C&bold=true",
            // label: "Partenaire officiel"  ← optionnel, sinon clé i18n holy.welcome.partner_label
        },
        rewardImageUrl: "https://gmedia.playstation.com/is/image/SIEPDC/ps5-product-thumbnail-01-en-14sep21?$facebook$",
        rewardTitle:    "PS5",
        rewardSubtitle: "Édition Squad",
    },

    /* ─── ÉTAPE SEGMENTATION ─────────────────────────────────────── */
    segmentation: {
        /**
         * label/desc optionnels — si absents, retombe sur les clés i18n
         * holy.segmentation.product_<id>_label / _desc
         */
        products: [
            { id: "pots",    imgUrl: "https://fr.holy.com/cdn/shop/files/Energy-Tub-Lovebird2x_800x.webp?v=1773757323" },
            { id: "shaker",  imgUrl: "https://fr.holy.com/cdn/shop/files/88_20_281_29_800x.png?v=1773394172" },
            { id: "box",     imgUrl: "https://fr.holy.com/cdn/shop/files/Trialbox_20Hydration_800x.png?v=1773394005" },
            { id: "sachets", imgUrl: "https://fr.holy.com/cdn/shop/files/HOLY_Energy_Sachet_Raptor_3d5jbh_800x.png?v=1773484884" },
        ],
    },

    /* ─── ÉCRAN DE RÉSULTAT ──────────────────────────────────────── */
    result: {
        confettiColors: ["#05D37C", "#FFEC28", "#FF91A2"],
        socials: [
            { platform: "tiktok",    url: "https://www.tiktok.com/@holy_fr" },
            { platform: "instagram", url: "https://www.instagram.com/holy_fr" },
            { platform: "twitter",   url: "https://twitter.com/holy_fr" },
            { platform: "facebook",  url: "https://www.facebook.com/holy.energy.fr" },
        ],
    },

    /* ─── DONNÉES DE LA DÉMO (spécifiques à ce client) ──────────── */
    demoContest: {
        fixture: {
            type: "FOOTBALL",
            id: 5,
            startingAt: "2025-12-13 15:00:00",
            label: "Finale",
            state: FIXTURE_STATE_CONFIG.SECOND_HALF,
            minutes: 81,
            extraMinutes: null,
            league: { name: "DEMO CUP", logoUrl: null },
        },
        coverUrl: "https://fr.holy.com/cdn/shop/files/How_to_prepare_HOLY_GIF_1200x.gif?v=1752236614",
        place: {
            id: 1,
            name: "Spot Burger Joint",
            address: "Halles Biltoki, 1 Rue Rouget de Lisle, 92130 Issy-les-Moulineaux, France",
            googlePlaceId: "ChIJJSXu4qx75kcRrkLEfNkp_60",
        },
        // Récompenses par défaut (overridées par rewardsByLang si la langue est trouvée)
        defaultRewardLabel: "Une boisson offerte",
        defaultWinnerReward: { title: "Pinte, Vin ou Soft",       type: REWARD_TYPE.PHYSICAL, code: "WIN-DRINK" },
        defaultLoserReward:  { title: "1 Shot de consolation",    type: REWARD_TYPE.PHYSICAL, code: "LOSE-SHOT" },
        // Récompenses localisées
        rewardsByLang: {
            fr: { rewardLabel: "Une PS5 à gagner",              winnerReward: { title: "1 PS5 Édition Squad",   type: REWARD_TYPE.PHYSICAL }, loserReward: { title: "-10% sur tout le site",          type: REWARD_TYPE.PHYSICAL, promoCode: "SQUAD-10" } },
            en: { rewardLabel: "A PS5 to win",                  winnerReward: { title: "1 PS5 Squad Edition",   type: REWARD_TYPE.PHYSICAL }, loserReward: { title: "-10% sitewide",                  type: REWARD_TYPE.PHYSICAL, promoCode: "SQUAD-10" } },
            es: { rewardLabel: "Una PS5 para ganar",            winnerReward: { title: "1 PS5 Edición Squad",   type: REWARD_TYPE.PHYSICAL }, loserReward: { title: "-10% en toda la web",            type: REWARD_TYPE.PHYSICAL, promoCode: "SQUAD-10" } },
            it: { rewardLabel: "Una PS5 in palio",              winnerReward: { title: "1 PS5 Edizione Squad",  type: REWARD_TYPE.PHYSICAL }, loserReward: { title: "-10% su tutto il sito",          type: REWARD_TYPE.PHYSICAL, promoCode: "SQUAD-10" } },
            pt: { rewardLabel: "Uma PS5 para ganhar",           winnerReward: { title: "1 PS5 Edição Squad",    type: REWARD_TYPE.PHYSICAL }, loserReward: { title: "-10% em todo o site",            type: REWARD_TYPE.PHYSICAL, promoCode: "SQUAD-10" } },
            de: { rewardLabel: "Eine PS5 zu gewinnen",          winnerReward: { title: "1 PS5 Squad Edition",   type: REWARD_TYPE.PHYSICAL }, loserReward: { title: "-10% auf der gesamten Website",  type: REWARD_TYPE.PHYSICAL, promoCode: "SQUAD-10" } },
        },
    },
};
